/**
 * Sign and equality for the decimal STRINGS the API sends for money
 * (`Ledger.entitize` convention: `"1200.0000"`, `"-0.5"`, `"0.0000"`).
 *
 * These exist so no screen has to reach for `parseFloat` to answer "is there a
 * gap?" or "which way does it point?". A binary float cannot hold most decimal
 * fractions, so `parseFloat` turns a real 0.0000000001 discrepancy into `0` and
 * a panel proudly reports "no difference" on a row that is genuinely out — the
 * exact failure the 1C reconciliation screen exists to catch.
 *
 * Everything here works on the digits themselves, so precision is irrelevant.
 */

/** `[sign, wholeDigits, fractionDigits]` — the shape every helper below parses to. */
const DECIMAL_PATTERN = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/** True for anything these helpers can reason about exactly. */
export function isDecimalString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text || text === '+' || text === '-' || text === '.') return false;
  return DECIMAL_PATTERN.test(text);
}

/**
 * -1, 0 or 1, decided on the digits rather than on a float.
 *
 * Null, blank and anything unparsable answer 0: callers use this to colour a
 * figure and to decide whether a row still differs, and "treat what I cannot
 * read as no movement" is the quiet direction to fail in. Use
 * {@link isDecimalString} first where the distinction matters.
 */
export function decimalSign(value: string | null | undefined): -1 | 0 | 1 {
  if (value == null) return 0;
  const match = DECIMAL_PATTERN.exec(String(value).trim());
  if (!match) return 0;
  const [, sign, whole, fraction] = match;
  // "-0.0000" is zero, and zero has no sign.
  if (!/[1-9]/.test(`${whole ?? ''}${fraction ?? ''}`)) return 0;
  return sign === '-' ? -1 : 1;
}

export function isZeroDecimal(value: string | null | undefined): boolean {
  return decimalSign(value) === 0;
}

export function isNegativeDecimal(value: string | null | undefined): boolean {
  return decimalSign(value) === -1;
}

/**
 * Exact equality across differing precision — `"1200"`, `"1200.0"` and
 * `"1200.0000"` are the same number, and the API is not consistent about how
 * many zeros it sends for the same field on different rows.
 */
export function decimalsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (a == null || b == null) return a == null && b == null;
  return normalizeDecimal(a) === normalizeDecimal(b);
}

/**
 * Canonical form for comparison only — never for display, which must keep the
 * precision the backend chose. Trailing fractional zeros and leading whole
 * zeros go, and every representation of zero collapses to `"0"`.
 */
export function normalizeDecimal(value: string): string {
  const match = DECIMAL_PATTERN.exec(String(value).trim());
  if (!match) return String(value).trim();
  const [, sign, whole, fraction] = match;
  const trimmedWhole = (whole ?? '').replace(/^0+(?=\d)/, '') || '0';
  const trimmedFraction = (fraction ?? '').replace(/0+$/, '');
  const digits = trimmedFraction
    ? `${trimmedWhole}.${trimmedFraction}`
    : trimmedWhole;
  if (!/[1-9]/.test(digits)) return '0';
  return `${sign === '-' ? '-' : ''}${digits}`;
}

/**
 * Exact `a − b` on decimal strings, at the larger of the two input scales — so
 * `"1200.0000" − "1100.0000"` is `"100.0000"`, matching what the API sends.
 * Returns null when either side is missing or unparsable.
 */
export function subtractDecimals(
  a: string | null | undefined,
  b: string | null | undefined
): string | null {
  if (!isDecimalString(a) || !isDecimalString(b)) return null;
  const left = toScaled(a);
  const right = toScaled(b);
  const scale = Math.max(left.scale, right.scale);
  const diff =
    left.units * pow10(scale - left.scale) -
    right.units * pow10(scale - right.scale);
  const negative = diff < BigInt(0);
  const digits = (negative ? -diff : diff).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${whole}${scale ? `.${fraction}` : ''}`;
}

function toScaled(value: string): { units: bigint; scale: number } {
  const [, sign, whole, fraction] = DECIMAL_PATTERN.exec(value.trim())!;
  const frac = fraction ?? '';
  const units = BigInt(`${whole || '0'}${frac}`);
  return { units: sign === '-' ? -units : units, scale: frac.length };
}

/** No BigInt literals or `**`: some tsconfigs here target below ES2020. */
function pow10(exponent: number): bigint {
  return BigInt(`1${'0'.repeat(exponent)}`);
}
